import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { UerPriceListUerRowQuery } from 'gql/graphql';
import { isNotEmpty } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RowData } from '../PriceListUER/types';
import { uerPriceListUerRow } from '../PriceListUER/uerQueries';

const REQUEST_ID = 'priceListUerRow';

export const useWorkScopes = (uerBaseRowId: RowData['id']) => {
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    const [resetWorkScopesData] = useResetStoreData({ requestId: REQUEST_ID });

    useEffect(
        () => () => {
            resetWorkScopesData(undefined);
        },
        [resetWorkScopesData],
    );

    const variables = useMemo(() => ({ uerBaseRowId }), [uerBaseRowId]);

    const [workScopesData, workScopesStatus] = useRequest({
        requestId: REQUEST_ID,
        query: uerPriceListUerRow,
        variables,
    });

    const workScopes = useMemo(
        () => workScopesData?.uerPriceListUerRow?.data?.workScopes ?? [],
        [workScopesData?.uerPriceListUerRow?.data?.workScopes],
    );

    const rowData = useMemo(() => {
        const hasActiveFilter = Object.values(filterValues).some(isNotEmpty);

        return hasActiveFilter
            ? workScopes.filter(({ description }) => filterValues.description.includes(description))
            : workScopes;
    }, [filterValues, workScopes]);

    const columns = useMemo<
        ColDef<NonNullable<NonNullable<UerPriceListUerRowQuery['uerPriceListUerRow']>['data']>['workScopes'][number]>[]
    >(
        () => [
            {
                field: 'number',
                headerName: '№ пп',
                width: 128,
                spanHeaderHeight: true,
            },
            {
                field: 'description',
                headerName: 'Наименование',
                flex: 1,
                spanHeaderHeight: true,
                resizable: false,
                wrapText: true,
                autoHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: workScopes.map(({ description }) => ({ label: description })),
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
        ],
        [changeFilterHandler, workScopes],
    );

    return useMemo(
        () => ({
            changeFilterHandler,
            columns,
            rowData,
            status: workScopesStatus,
            title: `Расценка: ${workScopesData?.uerPriceListUerRow?.data?.code}`,
        }),
        [changeFilterHandler, columns, rowData, workScopesData?.uerPriceListUerRow?.data?.code, workScopesStatus],
    );
};
